import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaCalendarAlt, FaChevronDown } from 'react-icons/fa';
import DatePicker from 'react-datepicker';

const FilterChip = ({ label, onRemove }) => (
    <div className="inline-flex items-center bg-gray-100 rounded-full px-3 py-1 mr-2 mb-2">
        <span className="text-sm text-gray-700">{label}</span>
        <button
            onClick={onRemove}
            className="ml-2 text-gray-500 hover:text-gray-700"
        >
            ✕
        </button>
    </div>
);

export const FilterSection = ({
    debouncedSearch,
    searchTerm,
    companyFilter,
    setCompanyFilter,
    brandFilter,
    setBrandFilter,
    availableCompanies,
    availableBrands,
    activeFilters,
    handleFilterChange,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    setSearchTerm,
    setPage,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localSearch, setLocalSearch] = useState(searchTerm || '');

    useEffect(() => {
        setLocalSearch(searchTerm || '');
    }, [searchTerm]);

    const handleReset = () => {
        setSearchTerm('');
        setCompanyFilter('');
        setBrandFilter('');
        setStartDate(null);
        setEndDate(null);
        setPage(1);

        // Clear active filters list
        handleFilterChange('company', '');
        handleFilterChange('brand', '');
        handleFilterChange('startDate', null);
        handleFilterChange('endDate', null);
    };

    return (
        <div className="bg-white rounded-lg shadow-md">
            {/* Collapsible Header */}
            <button
                type="button"
                className="w-full flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-100"
                onClick={() => setIsOpen(prev => !prev)}
            >
                <div className="flex items-center space-x-2">
                    <FaFilter className="text-gray-600" />
                    <span className="text-sm sm:text-base font-medium text-gray-800">
                        Filters
                    </span>
                </div>
                <FaChevronDown
                    className={`text-gray-500 transform transition-transform duration-200 ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>

            {/* Collapsible Content */}
            {isOpen && (
                <div className="p-2 sm:p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search campaigns..."
                                value={localSearch}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setLocalSearch(value);
                                    debouncedSearch(value);
                                }}
                                className="w-full pl-8 pr-2 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-xs sm:text-sm"
                            />
                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs sm:text-sm" />
                        </div>
                        <select
                            value={companyFilter}
                            onChange={(e) => {
                                setCompanyFilter(e.target.value);
                                handleFilterChange('company', e.target.value);
                                setPage(1);
                            }}
                            className="px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-xs sm:text-sm"
                        >
                            <option value="">All Companies</option>
                            {availableCompanies.map(company => (
                                <option key={company.id} value={company.id}>{company.name}</option>
                            ))}
                        </select>
                        <select
                            value={brandFilter}
                            onChange={(e) => {
                                setBrandFilter(e.target.value);
                                handleFilterChange('brand', e.target.value);
                                setPage(1);
                            }}
                            className="px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-xs sm:text-sm"
                        >
                            <option value="">All Brands</option>
                            {availableBrands.map(brand => (
                                <option key={brand.id} value={brand.id}>{brand.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Active Filters */}
                    {activeFilters.length > 0 && (
                        <div className="mb-4 flex flex-wrap gap-2">
                            {activeFilters.map((filter, index) => (
                                <FilterChip
                                    key={index}
                                    label={`${filter.type}: ${filter.value}`}
                                    onRemove={() => {
                                        if (filter.type === 'company') setCompanyFilter('');
                                        if (filter.type === 'brand') setBrandFilter('');
                                        handleFilterChange(filter.type, '');
                                    }}
                                />
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                            <div className="relative w-full sm:w-auto">
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => {
                                        setStartDate(date);
                                        handleFilterChange('startDate', date);
                                        setPage(1);
                                    }}
                                    placeholderText="Start Date"
                                    className="w-full sm:w-auto pl-9 pr-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-xs sm:text-sm"
                                    dateFormat="MMM d, yyyy"
                                    maxDate={new Date()}
                                    isClearable
                                />
                                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs sm:text-sm" />
                            </div>
                            <span className="text-gray-500 text-xs sm:text-sm">to</span>
                            <div className="relative w-full sm:w-auto">
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => {
                                        setEndDate(date);
                                        handleFilterChange('endDate', date);
                                        setPage(1);
                                    }}
                                    placeholderText="End Date"
                                    className="w-full sm:w-auto pl-9 pr-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-xs sm:text-sm"
                                    dateFormat="MMM d, yyyy"
                                    maxDate={new Date()}
                                    minDate={startDate}
                                    isClearable
                                />
                                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs sm:text-sm" />
                            </div>
                        </div>
                        <div className="flex justify-start sm:justify-end">
                            <button
                                onClick={handleReset}
                                className="flex items-center px-3 py-1.5 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors duration-200 text-xs sm:text-sm font-medium"
                            >
                                <FaFilter className="mr-2 text-xs sm:text-sm" />
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

